import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateOnly, humanize, money } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Button, Select, StatusBadge } from '@/components/ui/primitives';

interface InvoiceRow {
  id: string;
  number: string;
  context: string;
  status: string;
  createdAt: string;
  patient: { id: string; mrn: string; fullName: string; category: string } | null;
  walkInName: string | null;
  payer: { id: string; name: string } | null;
  totals: { netMinor: number; paidMinor: number; balanceMinor: number };
}

/** Cashier desk: invoices for the day and a way to raise a new one (SRS 9.2). */
export function BillingPage() {
  const { can } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const invoices = useQuery({
    queryKey: ['invoices', status, search],
    queryFn: () =>
      get<{ data: InvoiceRow[] }>(`/invoices${qs({ status: status || undefined, q: search || undefined, limit: 100 })}`),
  });

  return (
    <>
      <PageHeader
        title={tr('billing', 'billing')}
        description={tr('billing', 'invoices_and_their_outstanding_balances')}
        actions={can('invoice.create') && <Button onClick={() => navigate('/billing/invoices/new')}>{tr('billing', 'new_invoice')}</Button>}
      />

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('billing', 'invoice_number_patient_name_or_mrn') }}
        filters={
          <FilterField label={tr('billing', 'status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44">
              <option value="">{tr('billing', 'all_statuses')}</option>
              {['draft', 'issued', 'partially_paid', 'paid', 'cancelled', 'refunded'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status || search ? () => { setStatus(''); setSearch(''); } : undefined}
      />

      <DataTable
        rows={invoices.data?.data}
        loading={invoices.isLoading} refreshing={invoices.isFetching}
        error={invoices.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/billing/invoices/${row.id}`)}
        empty={tr('billing', 'no_invoices_match')}
        columns={[
          { header: tr('billing', 'invoice'), sort: (row) => row.number, cell: (row) => <span className="tabular font-medium">{row.number}</span> },
          {
            header: tr('billing', 'patient'),
            sort: (row) => row.patient?.fullName ?? row.walkInName ?? '',
            cell: (row) =>
              row.patient ? (
                <div>
                  <p>{row.patient.fullName}</p>
                  <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
                </div>
              ) : (
                <span className="text-muted-foreground">{row.walkInName ?? tr('billing', 'walk_in')}</span>
              ),
          },
          { header: tr('billing', 'context'), sort: (row) => row.context, cell: (row) => humanize(row.context) },
          { header: tr('billing', 'payer'), sort: (row) => row.payer?.name ?? '', cell: (row) => row.payer?.name ?? 'Self' },
          { header: tr('billing', 'date'), sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
          { header: tr('billing', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          { header: tr('billing', 'total'), align: 'right', sort: (row) => row.totals.netMinor, cell: (row) => money(row.totals.netMinor) },
          {
            header: tr('billing', 'balance'),
            align: 'right',
            sort: (row) => row.totals.balanceMinor,
            cell: (row) => (
              <span className={row.totals.balanceMinor > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'}>
                {money(row.totals.balanceMinor)}
              </span>
            ),
          },
        ]}
      />
    </>
  );
}
